import { atom, useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { RepliesState, PostType, UseRepliesReturn } from "./type";
import { socialDatabase } from "../../Data/Social/Database";
import * as socialAPI from "../../Data/Social";
import { CreatePostParams } from "../../Data/Social/types";
import { validatePost, postsState } from "./Posts";

// Replies state atom
export const repliesState = atom<RepliesState>({
    key: "repliesState" + randomString(4),
    default: {
        items: {},
        loading: false,
        error: null
    }
});

export const useReplies = (): UseRepliesReturn => {
    const [state, setState] = useRecoilState(repliesState);
    const resetReplies = useResetRecoilState(repliesState);
    const setPostsState = useSetRecoilState(postsState);

    // Update replies for a specific post
    const updatePostReplies = useCallback((postid: string, replies: PostType[]): void => {
        setState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: replies
            }
        }));
    }, [setState]);

    // Create a reply to a post
    const createReply = useCallback(async (parentPostId: string, content: string): Promise<PostType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const params: CreatePostParams = {
                content,
                parent_postid: parentPostId
            };
            
            const result = await socialAPI.createPost(params);
            
            if (result && validatePost(result)) {
                await socialDatabase.savePosts([result]);
                
                // Update replies state
                setState(prev => {
                    const currentReplies = prev.items[parentPostId] || [];
                    return {
                        ...prev,
                        items: {
                            ...prev.items,
                            [parentPostId]: [...currentReplies, result]
                        },
                        loading: false
                    };
                });
                
                // Also add to posts state for consistency
                setPostsState(prev => ({
                    ...prev,
                    items: { ...prev.items, [result.postid]: result }
                }));
                
                // Update parent post's reply count in posts state
                setPostsState(prev => {
                    if (prev.items[parentPostId]) {
                        return {
                            ...prev,
                            items: {
                                ...prev.items,
                                [parentPostId]: {
                                    ...prev.items[parentPostId],
                                    reply_count: prev.items[parentPostId].reply_count + 1
                                }
                            }
                        };
                    }
                    return prev;
                });
                
                return result;
            }
            
            setState(prev => ({ ...prev, error: "Failed to create reply", loading: false }));
            return null;
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to create reply",
                loading: false
            }));
            console.error('Failed to create reply:', error);
            return null;
        }
    }, [setState, setPostsState]);

    // Get replies for a specific post
    const getReplies = useCallback((postid: string): PostType[] => {
        return state.items[postid] || [];
    }, [state.items]);

    // Sync replies for a specific post from API
    const syncReplies = useCallback(async (postid: string): Promise<void> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            // Get posts with parent_postid = postid (replies to this post)
            const result = await socialAPI.listPosts({ 
                parent_postid: postid
            });
            
            if (result && Array.isArray(result)) {
                const validReplies = result.filter(validatePost);
                
                if (validReplies.length > 0) {
                    await socialDatabase.savePosts(validReplies);
                    
                    // Update replies state
                    setState(prev => ({
                        ...prev,
                        items: {
                            ...prev.items,
                            [postid]: validReplies
                        },
                        loading: false
                    }));
                    
                    // Also add to posts state for consistency
                    const newPosts = Object.fromEntries(validReplies.map(post => [post.postid, post]));
                    setPostsState(prev => ({
                        ...prev,
                        items: { ...prev.items, ...newPosts }
                    }));
                } else {
                    setState(prev => ({ ...prev, loading: false }));
                }
            } else {
                setState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to sync replies",
                loading: false
            }));
            console.error('Failed to sync replies:', error);
        }
    }, [setState, setPostsState]);

    return {
        postReplies: state.items,
        loading: state.loading,
        error: state.error,
        
        // Reply functions
        updatePostReplies,
        createReply,
        getReplies,
        syncReplies,

        reset: resetReplies
    };
};